import { FC, ReactNode, useCallback } from 'react'
import styles from './Pagination.module.scss'
import Icon from '../icon/Icon'
import Link from 'next/link'
import Box from '../box/Box'
import { useRouter } from 'next/router'
interface Props {
  totalPages: number
  currentPage: number
}
const Pagination: FC<Props> = ({ totalPages, currentPage }) => {
  const router = useRouter()
  const { page, ...restQueries } = router.query
  const isOfflineMode = process.env.OFFLINE_MODE === 'true'
  const siblingCount = 1
  const generatePageNumbers = useCallback((): (string | number)[] => {
    const pages: (string | number)[] = []
    if (currentPage < 5) {
      for (let i = 1; i <= Math.min(5, totalPages); i++) {
        pages.push(i)
      }
      if (totalPages > 5) {
        pages.push('...')
        pages.push(totalPages)
      }
      return pages
    }
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1)
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages)
    pages.push(1)
    if (leftSiblingIndex > 2) {
      pages.push('...')
    }
    for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
      pages.push(i)
    }
    if (rightSiblingIndex < totalPages - 1) {
      pages.push('...')
    }

    if (rightSiblingIndex < totalPages) {
      pages.push(totalPages)
    }

    return pages
  }, [currentPage, totalPages])

  const pageHref = (pageNumber: number) => ({
    pathname: router.pathname,
    query: {
      ...restQueries,
      ...(pageNumber !== 1 && { page: pageNumber }),
    },
  })

  const renderControl = (
    pageNumber: number,
    className: string,
    label: string,
    children: ReactNode,
  ) => {
    if (!isOfflineMode || pageNumber === 1) {
      return (
        <Link href={pageHref(pageNumber)}>
          <button className={className} aria-label={label}>
            {children}
          </button>
        </Link>
      )
    }

    return (
      <span className={className} aria-label={label}>
        {children}
      </span>
    )
  }

  return (
    <ul className={styles['pagination-container']}>
      {currentPage > 1 && (
        <li>
          {renderControl(
            currentPage - 1,
            styles['page-number'],
            'Go to previous page',
            <Icon name="arrow-right-1" type="highEmphasis" />,
          )}
        </li>
      )}

      {generatePageNumbers().map((pageNumber, index) => {
        return (
          <li key={'PAGINATION_ITEM_' + index}>
            {typeof pageNumber === 'number' ? (
              renderControl(
                pageNumber,
                `${styles['page-number']} ${
                  currentPage === pageNumber ? styles['active'] : ''
                }`,
                `Go to page ${pageNumber}`,
                pageNumber,
              )
            ) : (
              <Box className={styles['ellipsis']}>...</Box>
            )}
          </li>
        )
      })}

      {currentPage < totalPages && (
        <li>
          {renderControl(
            currentPage + 1,
            styles['page-number'],
            'Go to next page',
            <Icon name="arrow-left" type="highEmphasis" />,
          )}
        </li>
      )}
    </ul>
  )
}
export default Pagination
